import { useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  allDdRequestsKey,
  ALL_DD_STALE_MS,
  fetchAllDdRequests,
  otherItemsWaitingOn,
  // Single-item DD writes patch the whole-checklist cache (shared with the seller app).
  patchDdRequestInCache as patchDdRequest,
  answerQuestion,
  api,
  askQuestion,
  contractsQueryKey,
  correctContract,
  fetchContractRegister,
  rescanContracts,
  buyerQuestionsQueryKey,
  closeQuestion,
  documentSearchQueryKey,
  fetchDocumentSearch,
  hasSearchCriteria,
  listQuestions,
  documentsPollTracker,
  estimateQueryKey,
  estimateRefetchInterval,
  fetchProcessingEstimate,
  type ProcessingEstimate,
  nextDocumentsPoll,
  processingPollDocs,
  publishQuestion,
  questionsQueryKey,
  releaseAnswer,
  reopenQuestion,
  routeQuestion,
  unpublishQuestion,
  fetchVersionSuggestions,
  resolveVersionSuggestions,
  versionSuggestionsQueryKey,
  type BulkSupport,
  type VersionSuggestionAction,
  documentVersionsQueryKey,
  fetchDocumentVersions,
  versionsRefetchInterval,
  splitDocumentVersion,
  clearUnreadableFlag,
  uploadReplacement,
  type IngestFinalizeRequest,
} from '@dataroom/shared';
import {
  fetchProcessingQueue,
  processingQueueKey,
  type ProcessingQueueFilter,
  type ProcessingQueueResponse,
} from '@/lib/processing-queue';
import type {
  FolderAudience,
  SetFolderAudienceRequest,
  DocumentVisibilityRequest,
  DocumentVisibilityResponse,
  SetStatementBasisRequest,
  SetStatementBasisResponse,
  AccessGrant,
  AdvanceFunnelRequest,
  CancelEnvelopeResponse,
  CreateAccessGrantRequest,
  CreateAccessGrantResponse,
  UpdateGrantExpiryRequest,
  CreateRoomRequest,
  AdminSigningEnvelope,
  AnswerDdPhaseQuestionRequest,
  CreateDdAccountRequest,
  CreateDdAccountResponse,
  CreateDdPhaseQuestionRequest,
  DdAccount,
  ListDdAccountsResponse,
  ListDdStatesResponse,
  SetDdStatesResponse,
  UpdateDdAccountRequest,
  CreateDdPhaseRequest,
  CreateDdRequestRequest,
  DdPhase,
  DdPhaseQuestion,
  DdRequest,
  DdRequestsSummaryResponse,
  ListDdPhaseQuestionsResponse,
  UpdateDdPhaseQuestionRequest,
  ImportDdRequestsRequest,
  ImportDdRequestsResponse,
  CreateSavedChecklistRequest,
  InstantiateChecklistRequest,
  InstantiateChecklistResponse,
  InstantiateChecklistXlsxRequest,
  InstantiateChecklistXlsxResponse,
  ListSavedChecklistsResponse,
  SavedChecklistTemplate,
  AddSellerRequest,
  InvitePartyRequest,
  InvitePartyResponse,
  IssueEnvelopeRequest,
  ListAccessGrantsResponse,
  ListAdminSigningEnvelopesResponse,
  ListAuditLogResponse,
  AuditLogFilters,
  ListBuyersResponse,
  ListBuyerEngagementResponse,
  BuyerEngagementDetail,
  ListCategoriesResponse,
  ListCertificationsResponse,
  ListDdPhasesResponse,
  ListDdRequestsQuery,
  ListDdRequestsResponse,
  SetDdRequestRequiredRequest,
  SetDdRequestStatusRequest,
  UpdateDdRequestRequest,
  RenameDdPhaseRequest,
  ListDocumentsResponse,
  ListPendingProposalsResponse,
  ReviewProposalResponse,
  AddBuyerTeamMemberResponse,
  AdminBuyerTeamResponse,
  ListTeamRequestsResponse,
  Exclusivity,
  ExclusivityView,
  ExtendExclusivityRequest,
  ListLoisResponse,
  ListRoomsResponse,
  RoomAdminsResponse,
  RoomDashboardResponse,
  LoiDecisionRequest,
  LoiDecisionResponse,
  SetExclusivitySettingsRequest,
  ModifyDocumentAttributesRequest,
  ModifyDocumentAttributesResponse,
  SetDocumentDatingRequest,
  SetDocumentDatingResponse,
  RevokeGrantResponse,
  Room,
  RoomSellersResponse,
  SetSellerOwnershipRequest,
  SetTeamRolesRequest,
  SetTeamRolesResponse,
  TeamRole,
  RoomFunnelResponse,
  RenameRoomRequest,
  SearchDocumentsQuery,
  SessionResponse,
  SetRoomAdminRoleRequest,
  SetRoomFiscalCalendarRequest,
  SetMasterNdaRequest,
  SetDdPhaseReleaseDateRequest,
  SetDdPhaseDatesRequest,
  DeleteDdPhaseItems,
  DeleteDdPhaseResponse,
  SetRoomSellerLoiPermissionRequest,
  TransitionRoomLifecycleRequest,
  CreateSupportRequestRequest,
  SupportRequest,
  GrantTenantAdminRequest,
  ListTenantAdminsResponse,
  RecoveryContact,
  ClassificationLearningSetting,
  RevokeTenantAdminResponse,
  SetTenantOwnerResponse,
  TenantAdmin,
  BillingArchivePackRequest,
  BillingCheckoutRequest,
  BillingPricing,
  BillingRecoveryRequest,
  BillingUrlResponse,
  TenantPlan,
  BulkReclassifyRequest,
  BulkReclassifyResponse,
  ReclassifyDocumentResponse,
  RoomDocument,
  AnswerQuestionRequest,
  AskQuestionRequest,
  PublishQuestionRequest,
  QuestionView,
  CreateMilestoneRequest,
  CreateRiskRequest,
  DigestPreview,
  DigestSettings,
  DigestSettingsView,
  ActivityAlertSettings,
  ActivityAlertSettingsView,
  ListMilestonesResponse,
  ListRisksResponse,
  CorrectContractRequest,
  RoomMilestone,
  RoomRisk,
  SendDigestPreviewResponse,
  UpdateMilestoneRequest,
  UpdateRiskRequest,
} from '@dataroom/shared';
import { reclassifyNeedsReview, type NeedsReviewCounts } from '@/lib/classification-review';

/** A room member an admin can grant/invite, from GET /rooms/:roomId/actors —
 * everyone associated with the room (grant, funnel, signing envelope, or admin).
 * `suggestedRole` pre-fills the dialog from the member's latest grant role. */
export interface RoomActor {
  id: string;
  label: string;
  /** The party role this actor is on the room, for a sensible default. */
  suggestedRole: InvitePartyRequest['role'];
  /** True for the room's admin (the caller) — excluded from signer pickers. */
  isAdmin: boolean;
  /** True for the room's bound seller principal — excluded from signer pickers. */
  isSeller: boolean;
}

interface ListRoomActorsResponse {
  actors: RoomActor[];
}

/** Rooms in the caller's tenant. `retry: false` so a 401 surfaces immediately
 * (the RoomsPage redirects to /login on it) rather than retrying. */
export function useRooms() {
  return useQuery({
    queryKey: ['rooms'],
    queryFn: () => api.get<ListRoomsResponse>('/rooms').then((r) => r.rooms),
    retry: false,
  });
}

/** The rooms dashboard (GET /rooms/summary): per room the caller administers,
 * the deal's figures at a glance. Under the `['rooms']` prefix so anything that
 * refreshes the rooms list refreshes it too. */
export function useRoomsDashboard() {
  return useQuery({
    queryKey: ['rooms', 'summary'],
    queryFn: () => api.get<RoomDashboardResponse>('/rooms/summary').then((r) => r.rooms),
    retry: false,
  });
}

/** A burst of {@link invalidateRoomDocuments} calls (one per finished upload in
 * a 2,000-file batch) is coalesced to at most one refresh per this window. */
export const DOCUMENTS_REFRESH_WINDOW_MS = 2000;

interface RefreshState {
  /** When the last refresh actually ran. */
  last: number;
  /** The one pending trailing refresh, if a call arrived inside the window. */
  trailing?: Promise<unknown>;
}
const refreshState = new WeakMap<object, Map<string, RefreshState>>();

function refreshRoomDocuments(
  qc: ReturnType<typeof useQueryClient>,
  roomId: string,
): Promise<unknown> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['rooms', roomId, 'documents'] }),
    qc.invalidateQueries({ queryKey: ['rooms', roomId, 'search'] }),
    // The processing list and the room's "Processing N…" indicator: a filing, a
    // re-classify or a new upload changes what is still unfinished.
    qc.invalidateQueries({ queryKey: ['rooms', roomId, 'processing'] }),
  ]);
}

/** Refresh EVERY view of a room's documents: the plain listing and every
 * loaded search / folder (tagFilter) view, which cache under
 * ['rooms', id, 'search', …] and would otherwise keep showing a document in its
 * old folder. Call after anything that adds, re-files, approves/rejects or
 * re-classifies a document. Returns the refetch promise.
 *
 * Coalesced per room: the first call refreshes at once; calls inside the next
 * {@link DOCUMENTS_REFRESH_WINDOW_MS} share ONE trailing refresh at the end of
 * the window (so the final state is always fetched), and their returned promise
 * resolves when that refresh completes. A bulk upload therefore costs a refresh
 * every ~2 s, not three requests per file. */
export function invalidateRoomDocuments(
  qc: ReturnType<typeof useQueryClient>,
  roomId: string,
): Promise<unknown> {
  let rooms = refreshState.get(qc);
  if (!rooms) refreshState.set(qc, (rooms = new Map()));
  let state = rooms.get(roomId);
  if (!state) rooms.set(roomId, (state = { last: -Infinity }));
  const s = state;
  if (s.trailing) return s.trailing;
  const wait = s.last + DOCUMENTS_REFRESH_WINDOW_MS - Date.now();
  if (wait <= 0) {
    s.last = Date.now();
    return refreshRoomDocuments(qc, roomId);
  }
  s.trailing = new Promise((resolve) => {
    setTimeout(() => {
      s.trailing = undefined;
      s.last = Date.now();
      resolve(refreshRoomDocuments(qc, roomId));
    }, wait);
  });
  return s.trailing;
}

/** What the poll cadence {@link nextDocumentsPoll} remembers (in-flight
 * documents and when they last moved). Module-level: shared by every mounted
 * documents query, which all read the same cache entry. */
const documentsPoll = documentsPollTracker();

export interface RoomDocumentsOptions {
  /** An extra, faster poll this caller needs on top of the shared cadence —
   * read each time the next poll is scheduled (so it can read refs). The upload
   * panel uses it while its own uploads are not in the listing yet: the shared
   * cadence only starts once a fetched listing shows a document in flight. A
   * number polls at most that often; undefined/false adds nothing. */
  extraPoll?: () => number | false | undefined;
  /** False to hold the read until it is needed (default true). */
  enabled?: boolean;
}

/** The room's documents. Polls while ANY document is still in flight — every
 * 2 s while documents keep settling, backing off once nothing moves, giving up
 * after 10 minutes without progress and stopping on an error (see
 * {@link nextDocumentsPoll}) — and stops once none are, so
 * `Processing` badges advance to Classified / low-confidence / "Couldn't
 * classify" live, with no reload and no polling once the room is settled.
 *
 * When a poll sees a document LEAVE `processing`, its classify job has just
 * landed new AI proposals and (maybe) auto-filed categories, so the pending
 * proposals and the category tree are refreshed too — the review panel and the
 * tree counts then match the badge without a reload. */
export function useRoomDocuments(roomId: string, options: RoomDocumentsOptions = {}) {
  const qc = useQueryClient();
  const { extraPoll, enabled = true } = options;
  return useQuery({
    queryKey: ['rooms', roomId, 'documents'],
    enabled,
    queryFn: async () => {
      const before = qc.getQueryData<RoomDocument[]>(['rooms', roomId, 'documents']);
      const docs = await api
        .get<ListDocumentsResponse>(`/rooms/${roomId}/documents`)
        .then((r) => r.documents);
      if (before) {
        const nowById = new Map(docs.map((d) => [d.id, d.classificationStatus] as const));
        const settled = before.some(
          (d) => d.classificationStatus === 'processing' && nowById.get(d.id) !== 'processing',
        );
        if (settled) {
          void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'classification', 'pending'] });
          void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'categories'] });
          // …and may have found a possible new version of another document.
          void qc.invalidateQueries({ queryKey: versionSuggestionsQueryKey(roomId) });
          // A settled classification may have auto-filed the document into a
          // folder: refresh the search/folder views (not the listing itself —
          // this IS the listing's fetch).
          void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'search'] });
        }
      }
      return docs;
    },
    retry: false,
    refetchInterval: (query) => {
      const shared = nextDocumentsPoll(
        query.state.data,
        documentsPoll,
        Date.now(),
        query.state.status === 'error',
      );
      const extra = query.state.status === 'error' ? undefined : extraPoll?.();
      if (typeof extra !== 'number') return shared;
      return shared === false ? extra : Math.min(shared, extra);
    },
  });
}

/** The room's PROCESSING list (owner, 2026-10-15): one page of the documents
 * that have NOT finished every stage — still running, or waiting on a person —
 * from server state alone (it survives leaving the upload panel, a reload,
 * another device), with the room's counts. Polls on the shared cadence while a
 * stage is running anywhere in the room, so a document leaves the list live the
 * moment its last stage finishes; stops once nothing runs. Each mounted caller
 * (the page, the room header's indicator) keeps its own poll tracker. */
export function useRoomProcessingQueue(roomId: string, filter: ProcessingQueueFilter, page = 1) {
  const [tracker] = useState(documentsPollTracker);
  return useQuery<ProcessingQueueResponse>({
    queryKey: processingQueueKey(roomId, filter, page),
    queryFn: ({ signal }) => fetchProcessingQueue(roomId, filter, page, signal),
    retry: false,
    placeholderData: (prev) => prev,
    refetchInterval: (query) =>
      nextDocumentsPoll(
        processingPollDocs(query.state.data),
        tracker,
        Date.now(),
        query.state.status === 'error',
      ),
  });
}

/** The room's processing time estimate and per-stage tally (owner ask 2026-10-23;
 * GET /rooms/:id/processing/estimate): refreshes every ~10 s while work remains.
 * `enabled: false` reads nothing (the end-of-upload line asks only once a batch is done). */
export function useProcessingEstimate(roomId: string, options: { enabled?: boolean } = {}) {
  return useQuery<ProcessingEstimate>({
    queryKey: estimateQueryKey(roomId),
    queryFn: () => fetchProcessingEstimate(roomId),
    enabled: options.enabled ?? true,
    retry: false,
    refetchInterval: (query) =>
      estimateRefetchInterval(query.state.data, query.state.status === 'error'),
  });
}

/** The room's PENDING AI classification proposals (issue #131): every
 * sub-threshold document<->tag proposal still awaiting review, across the whole
 * room. The per-document detail view filters these to one document. Admin-only
 * server-side (a non-admin gets 404, surfaced as an ApiError). Polls on the same
 * cadence as the documents while classification is resolving, since a background
 * job files new proposals seconds after upload. */
export function usePendingProposals(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'classification', 'pending'],
    queryFn: () =>
      api
        .get<ListPendingProposalsResponse>(`/rooms/${roomId}/classification/pending`)
        .then((r) => r.pending),
    retry: false,
  });
}

/** Invalidate the three reads a proposal approve/reject affects: the documents
 * listing and every search/folder view (an approve auto-files an edge →
 * `categoryPath` changes), the category
 * tree (rolled-up counts change), and the pending queue itself (the reviewed
 * proposal drops out). */
function invalidateAfterReview(qc: ReturnType<typeof useQueryClient>, roomId: string): void {
  void invalidateRoomDocuments(qc, roomId);
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'categories'] });
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'classification', 'pending'] });
}

/** APPROVE one pending proposal (POST .../classification/:proposalId/approve):
 * promotes it to a live `document_tags` edge (the document files under that
 * category). A 409 means it was already reviewed; a 404 means it is gone/not in
 * this room. Invalidates documents + categories + pending on success. */
export function useApproveProposal(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (proposalId: string) =>
      api.post<ReviewProposalResponse>(`/rooms/${roomId}/classification/${proposalId}/approve`),
    onSuccess: () => invalidateAfterReview(qc, roomId),
  });
}

/** REJECT one pending proposal (POST .../classification/:proposalId/reject):
 * discards the proposal (no edge is created). Same 409/404 semantics and the
 * same invalidations as {@link useApproveProposal}. */
export function useRejectProposal(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (proposalId: string) =>
      api.post<ReviewProposalResponse>(`/rooms/${roomId}/classification/${proposalId}/reject`),
    onSuccess: () => invalidateAfterReview(qc, roomId),
  });
}

/** Re-classify ONE document (ADR-0018 §6, POST .../documents/:documentId/reclassify
 * → 202). Room admin only. The job re-runs asynchronously: refreshing the
 * listing brings the document back as `processing`, which re-arms the 2 s poll
 * in {@link useRoomDocuments} until it settles. A human's categorisation is
 * never touched by the re-run (server rule). */
export function useReclassifyDocument(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      api.post<ReclassifyDocumentResponse>(`/rooms/${roomId}/documents/${documentId}/reclassify`),
    onSuccess: () => invalidateRoomDocuments(qc, roomId),
  });
}

/**
 * "Upload a replacement" for a file we couldn't read (ADR-0051): a new version
 * of that document through the readable-copy path. The checklist re-reads too
 * (the new version may now count for its item).
 */
export function useUploadReplacement(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, body }: { documentId: string; body: IngestFinalizeRequest }) =>
      uploadReplacement(roomId, documentId, body),
    onSuccess: () =>
      Promise.all([
        invalidateRoomDocuments(qc, roomId),
        qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-requests'] }),
      ]),
  });
}

/** "It opens fine — clear the flag" (ADR-0051 update 2026-10-15): the admin's audited decision. */
export function useClearUnreadableFlag(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => clearUnreadableFlag(roomId, documentId),
    onSuccess: () =>
      Promise.all([
        invalidateRoomDocuments(qc, roomId),
        qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-requests'] }),
      ]),
  });
}

/** "Re-run AI on N documents": the bulk re-classify (POST .../documents/reclassify)
 * once per needs-review status present — see {@link reclassifyNeedsReview}. */
export function useReclassifyNeedsReview(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (counts: Pick<NeedsReviewCounts, 'lowConfidence' | 'failed'>) =>
      reclassifyNeedsReview(
        (body: BulkReclassifyRequest) =>
          api.post<BulkReclassifyResponse>(`/rooms/${roomId}/documents/reclassify`, body),
        counts,
      ),
    onSuccess: () => invalidateRoomDocuments(qc, roomId),
  });
}

/** PRD-0004 §4 "find a document": free-text + category-tag search, scoped to
 * the caller's grant server-side. Disabled (no request) until there is a query
 * or tag facet, so an empty search bar leaves the plain listing in charge. */
export function useDocumentSearch(roomId: string, params: SearchDocumentsQuery) {
  return useQuery({
    queryKey: documentSearchQueryKey(roomId, params),
    queryFn: () => fetchDocumentSearch(roomId, params),
    enabled: hasSearchCriteria(params),
    retry: false,
  });
}

export function useRoomCategories(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'categories'],
    queryFn: () => fetchRoomCategories(roomId),
    select: (r: ListCategoriesResponse) => r.categories,
    retry: false,
  });
}

/** The same read's checklist phases and order (display only — the folder tree
 * reads Phase › Category › … in the checklist's order, lib/folder-phases). One
 * request serves both hooks: the cache holds the whole response. */
export function useRoomFolderOrder(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'categories'],
    queryFn: () => fetchRoomCategories(roomId),
    select: (r: ListCategoriesResponse) => ({
      phases: r.phases ?? [],
      categoryOrder: r.categoryOrder ?? [],
    }),
    retry: false,
  });
}

function fetchRoomCategories(roomId: string): Promise<ListCategoriesResponse> {
  return api.get<ListCategoriesResponse>(`/rooms/${roomId}/categories`);
}

/** A new folder's default audience (ADR-0010 §2 tiers): the teaser tier goes to
 * every invited buyer; the CIM (`nda_signed`) and due-diligence tiers only by an
 * explicit grant. */
export type FolderTier = 'teaser' | 'nda_signed' | 'due_diligence';

/** Create folder(s) through the hierarchy import: at the top level with a tier,
 * or inside an existing folder (`parentTagId`), sharing its tier. */
export function useCreateFolder(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      nodes: { name: string; children?: unknown[] }[];
      unlockStage?: FolderTier;
      parentTagId?: string;
    }) => api.post<unknown>(`/rooms/${roomId}/tags/import`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'categories'] });
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'folder-audiences'] });
    },
  });
}

/** Every folder's audience (own + effective tier) for the admin's folder tree. */
export function useFolderAudiences(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'folder-audiences'],
    queryFn: () =>
      api
        .get<{ folders: FolderAudience[] }>(`/rooms/${roomId}/folder-audiences`)
        .then((r) => r.folders),
    retry: false,
  });
}

/** Change a folder's audience. A 409 carries advisory warnings (read them with
 * grantWarningsFromError) — the caller resends with acknowledgeWarnings. */
export function useSetFolderAudience(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tagId, body }: { tagId: string; body: SetFolderAudienceRequest }) =>
      api.put<{ folder: FolderAudience }>(`/rooms/${roomId}/folders/${tagId}/audience`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms', roomId, 'folder-audiences'] }),
  });
}

/** PRD-0004 §3i "Modify Document Attributes": admin-only category-tag
 * reassignment for one document. On success, the room's document listing and
 * category tree (rolled-up counts change) are invalidated so both refetch. */
export function useModifyDocumentAttributes(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      documentId,
      body,
    }: {
      documentId: string;
      body: ModifyDocumentAttributesRequest;
    }) =>
      api.patch<ModifyDocumentAttributesResponse>(`/rooms/${roomId}/documents/${documentId}`, body),
    onSuccess: () => {
      void invalidateRoomDocuments(qc, roomId);
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'categories'] });
      // The server records the decision on the proposals: a taken suggestion is
      // approved and a superseded one rejected, so the pending queue changes.
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'classification', 'pending'] });
    },
  });
}

/** "Manage document": a person sets or clears a document's period and/or date
 * of data (only the keys sent change; null clears). Recorded as the person's
 * decision, and the server closes any pending AI suggestion for that field, so
 * the listing and the pending queue both re-read. */
export function useSetDocumentDating(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, body }: { documentId: string; body: SetDocumentDatingRequest }) =>
      api.put<SetDocumentDatingResponse>(`/rooms/${roomId}/documents/${documentId}/dating`, body),
    onSuccess: () => {
      void invalidateRoomDocuments(qc, roomId);
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'classification', 'pending'] });
    },
  });
}

/** Admin: hide one document from the bound seller and/or from buyers
 * (ADR-0028). The listing, folder tree and every search re-read afterwards. */
export function useSetDocumentVisibility(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, body }: { documentId: string; body: DocumentVisibilityRequest }) =>
      api.patch<DocumentVisibilityResponse>(
        `/rooms/${roomId}/documents/${documentId}/visibility`,
        body,
      ),
    onSuccess: () => {
      void invalidateRoomDocuments(qc, roomId);
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'categories'] });
    },
  });
}

/** Correct a financial statement's accounting basis (ADR-0053) — audited server-side. */
export function useSetStatementBasis(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, body }: { documentId: string; body: SetStatementBasisRequest }) =>
      api.put<SetStatementBasisResponse>(
        `/rooms/${roomId}/documents/${documentId}/statement-basis`,
        body,
      ),
    onSuccess: () => invalidateRoomDocuments(qc, roomId),
  });
}

/** The room's open "Possible new version of …" suggestions (ADR-0031). */
export function useVersionSuggestions(roomId: string) {
  return useQuery({
    queryKey: versionSuggestionsQueryKey(roomId),
    queryFn: () => fetchVersionSuggestions(roomId),
    retry: false,
  });
}

/** "Merge as new version" / "Keep separate" on one or several suggestions (in
 * bulk when the server can, else one by one — resolveVersionSuggestions). Never
 * throws: each suggestion's outcome comes back. The listing, the folder views
 * and the suggestions all re-read afterwards. */
export function useResolveVersionSuggestions(roomId: string) {
  const qc = useQueryClient();
  const bulk = useRef<BulkSupport>({ supported: null });
  return useMutation({
    mutationFn: ({ ids, action }: { ids: readonly string[]; action: VersionSuggestionAction }) =>
      resolveVersionSuggestions(roomId, ids, action, bulk.current),
    onSettled: () => {
      void invalidateRoomDocuments(qc, roomId);
      void qc.invalidateQueries({ queryKey: versionSuggestionsQueryKey(roomId) });
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'categories'] });
    },
  });
}

/** A document's version history (ADR-0031): the room admin, co-admins and the bound seller. */
export function useDocumentVersions(roomId: string, documentId: string) {
  return useQuery({
    queryKey: documentVersionsQueryKey(roomId, documentId),
    queryFn: () => fetchDocumentVersions(roomId, documentId),
    retry: false,
    refetchInterval: (query) => versionsRefetchInterval(query.state.data),
  });
}

/** "Split back out" (room admin): the merged version becomes a document of its
 * own again (re-classified); the listing, folders, histories and suggestions re-read. */
export function useSplitDocumentVersion(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, versionId }: { documentId: string; versionId: string }) =>
      splitDocumentVersion(roomId, documentId, versionId),
    onSettled: () => {
      void invalidateRoomDocuments(qc, roomId);
      void qc.invalidateQueries({ queryKey: versionSuggestionsQueryKey(roomId) });
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'categories'] });
    },
  });
}

export function useCreateRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRoomRequest) => api.post<Room>('/rooms', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant-plan'] });
      return qc.invalidateQueries({ queryKey: ['rooms'] });
    },
  });
}

/** The single room's read model (adminRole + derived buyerPortalActive for the
 * §3a Config panel). There is no single-room GET, so this selects it out of the
 * rooms list query — one cached fetch shared with the rooms page. */
export function useRoom(roomId: string) {
  return useQuery({
    queryKey: ['rooms'],
    queryFn: () => api.get<ListRoomsResponse>('/rooms').then((r) => r.rooms),
    retry: false,
    select: (rooms: Room[]) => rooms.find((r) => r.id === roomId) ?? null,
  });
}

/** GET /auth/session — the signed-in actor and the rooms they are a principal
 * of, each with `isAdmin` (main admin or live co-admin: exactly the gate the
 * admin-only room settings use). The room header reads it to offer Rename only
 * to the room's own admins — a firm admin can open a colleague's room from the
 * list but cannot change its settings. */
export function useSession() {
  return useQuery({
    queryKey: ['auth', 'session'],
    queryFn: () => api.get<SessionResponse>('/auth/session'),
    retry: false,
  });
}

/** PATCH /rooms/:roomId/name — rename the room. Optimistic: the rooms list (which
 * the room header and the rooms dashboard both read) shows the new name at once
 * and rolls back if the API refuses; on settle the list and the session room list
 * are re-read so every view carries the stored name. */
export function useRenameRoom(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RenameRoomRequest) => api.patch<Room>(`/rooms/${roomId}/name`, body),
    onMutate: async ({ name }) => {
      await qc.cancelQueries({ queryKey: ['rooms'], exact: true });
      const previous = qc.getQueryData<Room[]>(['rooms']);
      if (previous) {
        qc.setQueryData<Room[]>(
          ['rooms'],
          previous.map((r) => (r.id === roomId ? { ...r, name: name.trim() } : r)),
        );
      }
      return { previous };
    },
    onError: (_error, _body, context) => {
      if (context?.previous) qc.setQueryData(['rooms'], context.previous);
    },
    onSuccess: (room) => {
      qc.setQueryData<Room[]>(['rooms'], (rooms) =>
        rooms?.map((r) => (r.id === room.id ? { ...r, ...room } : r)),
      );
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['rooms'], exact: true });
      void qc.invalidateQueries({ queryKey: ['auth', 'session'] });
    },
  });
}

/** PRD-0004 §3a Config: change the room's admin role. On a 409 the mutation
 * rejects with an ApiError carrying advisory `warnings`; the page reads them via
 * `grantWarningsFromError` and re-submits with `acknowledgeWarnings: true`. */
export function useSetRoomAdminRole(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SetRoomAdminRoleRequest) =>
      api.patch<Room>(`/rooms/${roomId}/admin-role`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms'] }),
  });
}

/** Fiscal calendar (migration 0057, ADR-0040): the room's fiscal year end +
 * timezone. Admin-only (404 for others); refused with a 409 once the room's
 * checklist exists (its period requests were generated from it). */
export function useSetRoomFiscalCalendar(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SetRoomFiscalCalendarRequest) =>
      api.patch<Room>(`/rooms/${roomId}/fiscal-calendar`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms'] }),
  });
}

/** Master NDA (migration 0027): set or clear the room's single master NDA
 * document. Admin-only (404 for others). Invalidates the rooms query so the
 * Agreements card re-reads it. */
export function useSetRoomMasterNda(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SetMasterNdaRequest) => api.patch<Room>(`/rooms/${roomId}/master-nda`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms'] }),
  });
}

/** Seller LOI permission (migration 0019): toggle whether the room's bound
 * seller principal may record the LOI responses (accept / counter / reject)
 * alongside the admin. Admin-only (404 for others). Invalidates the
 * rooms query so the Config card re-derives. */
export function useSetRoomSellerLoiPermission(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SetRoomSellerLoiPermissionRequest) =>
      api.patch<Room>(`/rooms/${roomId}/seller-loi-permission`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms'] }),
  });
}

/** Change the room's status (active/archived/trashed). Admin-only (404 for
 * others); an invalid transition or a plan-limit breach → 409. Invalidates the rooms query so the status
 * badge + available actions re-derive. */
export function useTransitionRoomLifecycle(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TransitionRoomLifecycleRequest) =>
      api.post<Room>(`/rooms/${roomId}/lifecycle`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant-plan'] });
      return qc.invalidateQueries({ queryKey: ['rooms'] });
    },
  });
}

/** Move ANY room to a lifecycle state (the roomId is a call variable, unlike
 * {@link useTransitionRoomLifecycle}). Used by the archived-limit dialog to delete
 * (trash) the oldest archived room. */
export function useTransitionAnyRoomLifecycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roomId, to }: { roomId: string; to: TransitionRoomLifecycleRequest['to'] }) =>
      api.post<Room>(`/rooms/${roomId}/lifecycle`, { to }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant-plan'] });
      return qc.invalidateQueries({ queryKey: ['rooms'] });
    },
  });
}

/** PRD-0004 §3b Access Rights: every grant in the room (live + revoked). */
export function useAccessGrants(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'access-grants'],
    queryFn: () =>
      api.get<ListAccessGrantsResponse>(`/rooms/${roomId}/access-grants`).then((r) => r.grants),
    retry: false,
  });
}

/** Everything a change to who the sellers are (or what they own) can move. */
function invalidateSellers(qc: ReturnType<typeof useQueryClient>, roomId: string) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['rooms', roomId, 'sellers'] }),
    qc.invalidateQueries({ queryKey: ['rooms'], exact: true }),
    qc.invalidateQueries({ queryKey: ['rooms', roomId, 'access-grants'] }),
    qc.invalidateQueries({ queryKey: ['rooms', roomId, 'actors'] }),
    qc.invalidateQueries({ queryKey: ['rooms', roomId, 'lois'] }),
    qc.invalidateQueries({ queryKey: ['rooms', roomId, 'exclusivity'] }),
  ]);
}

/** The room's SELLERS and what each owns (GET /rooms/:roomId/sellers — the admin
 * and the sellers only; anyone else gets a 404, so there is nothing to show). */
export function useRoomSellers(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'sellers'],
    queryFn: () => api.get<RoomSellersResponse>(`/rooms/${roomId}/sellers`),
    retry: false,
  });
}

/** Add a seller by email with their ownership percentage (POST /rooms/:roomId/sellers).
 * The percentage may be left out only for the room's first seller (100%). A 409
 * means the sellers cannot change while a vote or an exclusivity is open. */
export function useAddSeller(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddSellerRequest) =>
      api.post<RoomSellersResponse>(`/rooms/${roomId}/sellers`, body),
    onSuccess: () => invalidateSellers(qc, roomId),
  });
}

/** Set every seller's percentage in one go (PUT /rooms/:roomId/sellers/ownership);
 * the whole set must total exactly 100. */
export function useSetSellerOwnership(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SetSellerOwnershipRequest) =>
      api.put<RoomSellersResponse>(`/rooms/${roomId}/sellers/ownership`, body),
    onSuccess: () => invalidateSellers(qc, roomId),
  });
}

/** Take one seller off the room (DELETE /rooms/:roomId/sellers/:actorId). The rest
 * keep their percentages, so the total may no longer be 100. */
export function useRemoveSeller(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (actorId: string) =>
      api.delete<RoomSellersResponse>(`/rooms/${roomId}/sellers/${actorId}`),
    onSuccess: () => invalidateSellers(qc, roomId),
  });
}

/** Set a team member's roles (PUT /rooms/:roomId/team-roles/:actorId): a seller's-team
 * or buyer-team person; an empty list clears them. */
export function useSetTeamRoles(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ actorId, ...body }: { actorId: string } & SetTeamRolesRequest) =>
      api.put<SetTeamRolesResponse>(`/rooms/${roomId}/team-roles/${actorId}`, body),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['rooms', roomId, 'access-grants'] }),
        qc.invalidateQueries({ queryKey: ['rooms', roomId, 'buyers'] }),
      ]),
  });
}

/** Invite a party with the role's invite-time default access (ADR-0010 §2). */
export function useInviteParty(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InvitePartyRequest) =>
      api.post<InvitePartyResponse>(`/rooms/${roomId}/access-grants/invite`, body),
    // The invitee is now a room member (the actor pickers list grant holders)
    // and, for a buyer, in the CIM pipeline.
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['rooms', roomId, 'access-grants'] }),
        qc.invalidateQueries({ queryKey: ['rooms', roomId, 'actors'] }),
        qc.invalidateQueries({ queryKey: ['rooms', roomId, 'buyers'] }),
      ]),
  });
}

/** Advanced add-grant (ADR-0010 §3 advise+acknowledge): rejects with an
 * ApiError carrying `warnings` on a 409; echoes acknowledged `warnings` on 201. */
export function useCreateAccessGrant(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAccessGrantRequest) =>
      api.post<CreateAccessGrantResponse>(`/rooms/${roomId}/access-grants`, body),
    // A grant makes its holder a room member (the actor pickers list them).
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['rooms', roomId, 'access-grants'] }),
        qc.invalidateQueries({ queryKey: ['rooms', roomId, 'actors'] }),
      ]),
  });
}

/** Revoke one live grant. Also refreshes the CIM buyers panel, whose per-buyer
 * "expires" cell is derived from that buyer's live grants. */
export function useRevokeGrant(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (grantId: string) =>
      api.post<RevokeGrantResponse>(`/access-grants/${grantId}/revoke`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'access-grants'] });
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'buyers'] });
    },
  });
}

/** §3e buyer-detail: update ONE grant's expiry — set a future `expiresAt` or make
 * it ongoing (clears the expiry). Admin-only server-side (404 for others / a grant
 * not in the room); a revoked grant -> 409; a past/absent expiry -> 400. Refreshes
 * the grants list AND the CIM buyers panel (its "expires" cell is derived). */
export function useUpdateGrantExpiry(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ grantId, body }: { grantId: string; body: UpdateGrantExpiryRequest }) =>
      api.patch<AccessGrant>(`/rooms/${roomId}/access-grants/${grantId}/expiry`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'access-grants'] });
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'buyers'] });
    },
  });
}

/** Room members for the grant/invite pickers — everyone associated with the
 * room (grant, funnel, envelope, or admin). Admin-only server-side
 * (GET /rooms/:roomId/actors); the dialogs still allow manual actor-id entry as
 * a fallback. */
export function useRoomActors(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'actors'],
    queryFn: () => api.get<ListRoomActorsResponse>(`/rooms/${roomId}/actors`).then((r) => r.actors),
    retry: false,
  });
}

/** PRD-0004 §3h LOI management: every LOI in the room with the submitting
 * buyer's funnel position (stage + exclusivity/closing dates) and resolved
 * email. Admin-only server-side. */
export function useRoomLois(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'lois'],
    queryFn: () => api.get<ListLoisResponse>(`/rooms/${roomId}/lois`).then((r) => r.lois),
    retry: false,
  });
}

/** PRD-0004 §3h / ADR-0029: record Accept / Counter / Reject on a submitted LOI.
 * An accept carries its exclusivity terms (ADR-0046) and, entered by the admin in
 * a room with a bound seller, waits for the seller's validation. A 409 carries an
 * exclusivity conflict or an already-decided LOI; the page surfaces the message.
 * Invalidates the LOI listing and the exclusivity view on success. */
export function useDecideLoi(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loiId, body }: { loiId: string; body: LoiDecisionRequest }) =>
      api.post<LoiDecisionResponse>(`/rooms/${roomId}/loi/${loiId}/decision`, body),
    onSuccess: () => invalidateExclusivity(qc, roomId),
  });
}

/** ADR-0046 enforced exclusivity: the room's live exclusivity as the admin sees
 * it (pending / in effect, the buyer, the countdown and the clock settings). */
export function useRoomExclusivity(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'exclusivity'],
    queryFn: () => api.get<ExclusivityView>(`/rooms/${roomId}/exclusivity`),
    retry: false,
  });
}

/** Everything an exclusivity change can move: the view, the LOI list (status +
 * exclusivity column), the buyers (stage) and the room (clock settings). */
function invalidateExclusivity(qc: ReturnType<typeof useQueryClient>, roomId: string) {
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'exclusivity'] });
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'lois'] });
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'buyers'] });
}

/** Extend the in-effect exclusivity (the admin's waits for the seller's validation). */
export function useExtendExclusivity(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ExtendExclusivityRequest) =>
      api.post<Exclusivity>(`/rooms/${roomId}/exclusivity/extension`, body),
    onSuccess: () => invalidateExclusivity(qc, roomId),
  });
}

/** A buyer's NAMED team (migration 0070) and whose team they are on — admin only. */
export function useBuyerTeam(roomId: string, actorId: string | null) {
  return useQuery({
    queryKey: ['rooms', roomId, 'buyers', actorId, 'team'],
    queryFn: () => api.get<AdminBuyerTeamResponse>(`/rooms/${roomId}/buyers/${actorId}/team`),
    enabled: actorId !== null,
  });
}

/** Add a person (any email domain) to a buyer's team. Audited; they are invited. */
export function useAddBuyerTeamMember(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    // `roles`: the person's team roles (Legal, Financial, …). `expiresAt`: when the
    // new member's access ends — required by the server (a buyer's team never has
    // ongoing access).
    mutationFn: ({
      actorId,
      email,
      roles,
      expiresAt,
    }: {
      actorId: string;
      email: string;
      roles?: TeamRole[];
      expiresAt?: string;
    }) =>
      api.post<AddBuyerTeamMemberResponse>(`/rooms/${roomId}/buyers/${actorId}/team`, {
        email,
        ...(roles && roles.length > 0 ? { roles } : {}),
        expiresAt,
      }),
    onSuccess: () => invalidateExclusivity(qc, roomId),
  });
}

/** The room's waiting team asks from buyers (admin only), each with its problem if any. */
export function useTeamRequests(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'team-requests'],
    queryFn: () =>
      api.get<ListTeamRequestsResponse>(`/rooms/${roomId}/team-requests`).then((r) => r.requests),
  });
}

/** Approve (the person is added and invited) or decline a buyer's team ask. Audited. */
export function useDecideTeamRequest(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    // Approving needs the date the new member's access ends (never ongoing).
    mutationFn: ({
      requestId,
      verdict,
      expiresAt,
    }: {
      requestId: string;
      verdict: 'approve' | 'decline';
      expiresAt?: string;
    }) =>
      api.post<unknown>(
        `/rooms/${roomId}/team-requests/${requestId}/${verdict}`,
        verdict === 'approve' ? { expiresAt } : undefined,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'team-requests'] });
      invalidateExclusivity(qc, roomId);
    },
  });
}

/** Take a person off a buyer's team (their team access ends). Audited. */
export function useRemoveBuyerTeamMember(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ actorId, memberId }: { actorId: string; memberId: string }) =>
      api.delete<{ ok: boolean }>(`/rooms/${roomId}/buyers/${actorId}/team/${memberId}`),
    onSuccess: () => invalidateExclusivity(qc, roomId),
  });
}

/** End the in-effect exclusivity early (restores the paused buyers). Admin-only. */
export function useEndExclusivity(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<Exclusivity>(`/rooms/${roomId}/exclusivity/end`),
    onSuccess: () => invalidateExclusivity(qc, roomId),
  });
}

/** Who sees the countdown besides admin + seller. Admin-only; audited. */
export function useSetExclusivitySettings(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SetExclusivitySettingsRequest) =>
      api.patch<Room>(`/rooms/${roomId}/exclusivity-settings`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rooms'] });
    },
  });
}

/** PRD-0004 §1.1/§3f-g: admin advances a buyer's funnel one legal step, setting
 * the date for the dated post-LOI steps (exclusivity_expires / closing). The
 * LOI listing carries each buyer's funnel state, so it refetches on success. */
export function useAdvanceFunnel(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ actorId, body }: { actorId: string; body: AdvanceFunnelRequest }) =>
      api.post<RoomFunnelResponse>(`/rooms/${roomId}/funnel/${actorId}/advance`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'lois'] });
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'buyers'] });
    },
  });
}

/** §3e buyer-detail: admin moves a buyer's funnel BACK one legal step (the inverse
 * of advance; no body). A 409 means the buyer is already at the first stage. The
 * buyers + LOI panels carry the funnel state, so both refetch on success. */
export function useRollbackFunnel(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (actorId: string) =>
      api.post<RoomFunnelResponse>(`/rooms/${roomId}/funnel/${actorId}/rollback`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'buyers'] });
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'lois'] });
    },
  });
}

/** PRD-0004 §3e CIM management: every buyer in the room's funnel with the
 * derived NDA/CIM/all-files gates — "who can currently see the CIM".
 * Admin-only server-side. */
export function useRoomBuyers(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'buyers'],
    queryFn: () => api.get<ListBuyersResponse>(`/rooms/${roomId}/buyers`).then((r) => r.buyers),
    retry: false,
  });
}

/** ADR-0038 buyer engagement: one row per buyer — last active, documents
 * opened, views / downloads, % of their visible documents opened. Plain counts
 * from the audit log. Admin-only server-side. Keyed under `buyers` so a funnel
 * change that refreshes the buyer list refreshes this too. */
export function useRoomBuyerEngagement(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'buyers', 'engagement'],
    queryFn: () =>
      api
        .get<ListBuyerEngagementResponse>(`/rooms/${roomId}/buyers/engagement`)
        .then((r) => r.buyers),
    retry: false,
  });
}

/** ADR-0038: one buyer's engagement view (documents opened, rollup by area,
 * NDA / LOI milestones). Disabled until a buyer is selected. */
export function useBuyerEngagement(roomId: string, actorId: string | null) {
  return useQuery({
    queryKey: ['rooms', roomId, 'buyers', 'engagement', actorId],
    queryFn: () => api.get<BuyerEngagementDetail>(`/rooms/${roomId}/buyers/${actorId}/engagement`),
    enabled: actorId !== null,
    retry: false,
  });
}

/** PRD-0004 §3c NDA management: every NC/NDA signing envelope in the room —
 * who was issued what and who has signed. Admin-only server-side. */
export function useRoomEnvelopes(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'envelopes'],
    queryFn: () =>
      api
        .get<ListAdminSigningEnvelopesResponse>(`/rooms/${roomId}/signing/envelopes/all`)
        .then((r) => r.envelopes),
    retry: false,
  });
}

/** Invalidate the three coupled DD reads after any mutation: the paged request
 * pages (prefix-matched, so every loaded phase/offset slice refetches), the
 * per-phase progress rollup, and the phase list. Keeps the released badges,
 * progress bars, and item rows in sync from one place. */
function invalidateDd(qc: ReturnType<typeof useQueryClient>, roomId: string): void {
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-requests'] });
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-summary'] });
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-phases'] });
}

/** "requested documents checklist" slice 6: the room's checklist PHASES
 * (GET /:roomId/dd-phases → `{ phases }`), ordered by position with each phase's
 * `releasedAt`. Admin-only server-side. Feeds the phase grouping, the release
 * controls, and the "add item" phase picker. */
export function useDdPhases(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'dd-phases'],
    queryFn: () =>
      api.get<ListDdPhasesResponse>(`/rooms/${roomId}/dd-phases`).then((r) => r.phases),
    retry: false,
  });
}

/** Slice 6: the per-phase progress rollup (GET /:roomId/dd-requests/summary) —
 * the PRIMARY progress surface: one entry per phase (plus the admin's phaseless
 * legacy bucket) with `total`/`provided`/`due` + `released`. Admin-or-seller
 * server-side; the admin sees every phase (no release-gating). */
export function useDdSummary(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'dd-summary'],
    queryFn: () =>
      api
        .get<DdRequestsSummaryResponse>(`/rooms/${roomId}/dd-requests/summary`)
        .then((r) => r.phases),
    retry: false,
  });
}

/** Slice 6 (slice 4 shape): the room's requested-items list, PAGED
 * (GET /:roomId/dd-requests). Returns the FULL response (`requests` + `total`)
 * so the caller can page a large phase from `offset`/`limit`. The params ride in
 * the query key so each phase/offset slice caches independently; a mutation
 * invalidates the `['rooms', roomId, 'dd-requests']` prefix, refetching them all. */
export function useDdRequests(roomId: string, params: ListDdRequestsQuery = {}) {
  return useQuery({
    queryKey: ['rooms', roomId, 'dd-requests', params],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (params.phaseId) qs.set('phaseId', params.phaseId);
      if (params.status) qs.set('status', params.status);
      if (params.limit != null) qs.set('limit', String(params.limit));
      if (params.offset != null) qs.set('offset', String(params.offset));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return api.get<ListDdRequestsResponse>(`/rooms/${roomId}/dd-requests${suffix}`);
    },
    retry: false,
  });
}

/** The room's WHOLE checklist in one query (checklist redesign slice 1): the paged
 * list read at its largest page size — page 1 says `total`, then the remaining
 * pages are read in parallel (3 calls for the ~460-item default). Counts, category
 * counts, search and filters are then exact, client-side. It is the costliest read
 * on the page (database egress), so it is not refetched on window focus, stays
 * fresh for {@link ALL_DD_STALE_MS}, and a single-item write patches it rather than
 * refetching it. Shares the `dd-requests` key prefix, so a structural DD mutation
 * (invalidateDd) still refetches it. */
export function useAllDdRequests(roomId: string) {
  return useQuery({
    queryKey: allDdRequestsKey(roomId),
    queryFn: () => fetchAllDdRequests(roomId),
    staleTime: ALL_DD_STALE_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** Slice 6: create a checklist phase (POST /:roomId/dd-phases). `position` is
 * auto-assigned server-side. Refetches phases + summary on success. */
export function useCreateDdPhase(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDdPhaseRequest) =>
      api.post<DdPhase>(`/rooms/${roomId}/dd-phases`, body),
    onSuccess: () => invalidateDd(qc, roomId),
  });
}

/** Slice 6: RELEASE a phase (POST /:roomId/dd-phases/:phaseId/release) — stamps
 * `releasedAt`, making the phase's items reach the seller's checklist.
 * Intermediary-only and effectively one-way, so the card confirms inline. */
export function useReleaseDdPhase(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (phaseId: string) =>
      api.post<DdPhase>(`/rooms/${roomId}/dd-phases/${phaseId}/release`),
    onSuccess: () => invalidateDd(qc, roomId),
  });
}

/** Migration 0026: set (YYYY-MM-DD) or clear (null) a phase's scheduled trigger
 * date. A past-or-today date releases the phase to the seller immediately; the
 * summary/phases refresh so the badge reflects the new effective state. */
export function useSetPhaseReleaseDate(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ phaseId, body }: { phaseId: string; body: SetDdPhaseReleaseDateRequest }) =>
      api.patch<DdPhase>(`/rooms/${roomId}/dd-phases/${phaseId}/release-date`, body),
    onSuccess: () => invalidateDd(qc, roomId),
  });
}

/** Migration 0097: set a phase's date range — start (`releaseOn`) and/or completion
 * (`completeOn`) — in one write. The phase and summary reads refresh, so the range
 * shows everywhere and every inheriting item's deadline moves with it. */
export function useSetPhaseDates(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ phaseId, body }: { phaseId: string; body: SetDdPhaseDatesRequest }) =>
      api.patch<DdPhase>(`/rooms/${roomId}/dd-phases/${phaseId}/dates`, body),
    onSuccess: () => invalidateDd(qc, roomId),
  });
}

/** Delete a phase (admin-only). An empty phase needs no `items`; one that holds
 * items is told to move them to another phase or delete them too. Questions go
 * with the phase. */
export function useDeleteDdPhase(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ phaseId, items }: { phaseId: string; items?: DeleteDdPhaseItems }) => {
      const qs =
        items?.action === 'move'
          ? `?items=move&toPhaseId=${items.toPhaseId}`
          : items?.action === 'delete'
            ? '?items=delete'
            : '';
      return api.delete<DeleteDdPhaseResponse>(`/rooms/${roomId}/dd-phases/${phaseId}${qs}`);
    },
    onSuccess: () => {
      invalidateDd(qc, roomId);
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-questions'] });
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'certifications'] });
    },
  });
}

/** §3f/slice 6: create a DD request. `phaseId` files it under a phase; `stage`
 * stays for back-compat. Refetches the paged pages + summary + phases. */
export function useCreateDdRequest(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDdRequestRequest) =>
      api.post<DdRequest>(`/rooms/${roomId}/dd-requests`, body),
    onSuccess: () => invalidateDd(qc, roomId),
  });
}

/** Slice 6: BULK IMPORT hundreds of requested items across phases in one call
 * (POST /:roomId/dd-requests/import). Idempotent server-side (phases found-or-
 * created by name, requests by (phase, title)); returns the created/reused
 * counts. Refetches everything on success. */
export function useImportDdRequests(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ImportDdRequestsRequest) =>
      api.post<ImportDdRequestsResponse>(`/rooms/${roomId}/dd-requests/import`, body),
    onSuccess: () => invalidateDd(qc, roomId),
  });
}

/** After a room's checklist is seeded: the phases/items/summary AND the category
 * tree (seeding creates nested category tags) all change. */
function invalidateSeeded(qc: ReturnType<typeof useQueryClient>, roomId: string): void {
  invalidateDd(qc, roomId);
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'categories'] });
}

/** ADR-0017: SEED an empty room's checklist from the built-in default or a tenant
 * saved checklist (POST /:roomId/dd-requests/instantiate). A one-time seed — a
 * room that already has phases → 409; `saved` needs tenant admin (403). */
export function useInstantiateChecklist(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InstantiateChecklistRequest) =>
      api.post<InstantiateChecklistResponse>(`/rooms/${roomId}/dd-requests/instantiate`, body),
    onSuccess: () => invalidateSeeded(qc, roomId),
  });
}

/** ADR-0017: SEED an empty room's checklist from an uploaded .xlsx
 * (POST /:roomId/dd-requests/instantiate-xlsx). A workbook that can't be read is a
 * 422 `invalid_checklist` carrying row-level `problems`; with `saveAs` the
 * checklist is also saved for reuse (refreshes the saved list). */
export function useInstantiateChecklistXlsx(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InstantiateChecklistXlsxRequest) =>
      api.post<InstantiateChecklistXlsxResponse>(
        `/rooms/${roomId}/dd-requests/instantiate-xlsx`,
        body,
      ),
    onSuccess: (res) => {
      invalidateSeeded(qc, roomId);
      if (res.savedTemplate) void qc.invalidateQueries({ queryKey: ['dd-templates'] });
    },
  });
}

/** The tenant's SAVED checklists (GET /dd-templates). Tenant-admin only: a 403
 * is the signal to hide every saved-checklist affordance, so no retry. */
export function useSavedChecklists() {
  return useQuery({
    queryKey: ['dd-templates'],
    queryFn: () => api.get<ListSavedChecklistsResponse>('/dd-templates').then((r) => r.templates),
    retry: false,
  });
}

/** Upload an .xlsx and save it as a named tenant checklist (POST /dd-templates). */
export function useCreateSavedChecklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSavedChecklistRequest) =>
      api.post<SavedChecklistTemplate>('/dd-templates', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dd-templates'] }),
  });
}

/** Delete a saved checklist (rooms already seeded from it are unaffected). */
export function useDeleteSavedChecklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/dd-templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dd-templates'] }),
  });
}

/** §3f: toggle a DD request's status (open ↔ completed) — the admin accept/waive
 * override, distinct from the derived `provided`. Patches the item in place;
 * refetches the summary. */
export function useSetDdRequestStatus(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, body }: { requestId: string; body: SetDdRequestStatusRequest }) =>
      api.patch<DdRequest>(`/rooms/${roomId}/dd-requests/${requestId}`, body),
    onSuccess: (updated) => patchDdRequest(qc, roomId, updated),
  });
}

/** Checklist redesign slice 7: edit an item — title, detail, own due date, phase,
 * category (PATCH /:roomId/dd-requests/:requestId). An edit that keeps the item
 * where it is patches it in place; one that MOVES it — another phase or category,
 * or a new due date (the list is ordered by it) — refetches the whole checklist,
 * so it lands in the server's order. */
export function useUpdateDdRequest(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, body }: { requestId: string; body: UpdateDdRequestRequest }) =>
      api.patch<DdRequest>(`/rooms/${roomId}/dd-requests/${requestId}`, body),
    onSuccess: (updated) => {
      const before = qc
        .getQueryData<DdRequest[]>(allDdRequestsKey(roomId))
        ?.find((r) => r.id === updated.id);
      const moved =
        !before ||
        before.phaseId !== updated.phaseId ||
        JSON.stringify(before.categoryPath) !== JSON.stringify(updated.categoryPath) ||
        before.dueDate !== updated.dueDate;
      if (moved) {
        invalidateDd(qc, roomId);
        // A new category path may have created category tags.
        void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'categories'] });
        void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'certifications'] });
      } else {
        patchDdRequest(qc, roomId, updated);
      }
    },
  });
}

/** Checklist redesign slice 7: rename a phase (PATCH /:roomId/dd-phases/:phaseId). */
export function useRenameDdPhase(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ phaseId, body }: { phaseId: string; body: RenameDdPhaseRequest }) =>
      api.patch<DdPhase>(`/rooms/${roomId}/dd-phases/${phaseId}`, body),
    onSuccess: () => invalidateDd(qc, roomId),
  });
}

/** §3f (migration 0023): set a request's INTERMEDIARY "required" flag per Data
 * Room (PATCH /:roomId/dd-requests/:requestId/required, admin-only). An OPTIONAL
 * item is still shown but never counts toward "still due". Patches the item in
 * place; refetches the summary so the required-aware progress reconciles. */
export function useSetDdRequestRequired(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, body }: { requestId: string; body: SetDdRequestRequiredRequest }) =>
      api.patch<DdRequest>(`/rooms/${roomId}/dd-requests/${requestId}/required`, body),
    onSuccess: (updated) => patchDdRequest(qc, roomId, updated),
  });
}

/** §3f: delete a DD request. Refetches pages + summary + phases. */
export function useDeleteDdRequest(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) =>
      api.delete<{ ok: true }>(`/rooms/${roomId}/dd-requests/${requestId}`),
    onSuccess: () => invalidateDd(qc, roomId),
  });
}

/** Link a document already in the room to a checklist item
 * (POST /:roomId/dd-requests/:requestId/documents, admin-or-seller). The item
 * becomes covered and is patched in place. The API also settles the AI's
 * suggestions of that document on EVERY item, so if another cached item was
 * waiting on it the whole checklist refetches instead. */
export function useLinkDdDocument(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, documentId }: { requestId: string; documentId: string }) =>
      api.post<DdRequest>(`/rooms/${roomId}/dd-requests/${requestId}/documents`, { documentId }),
    onSuccess: (updated, { documentId }) => {
      if (otherItemsWaitingOn(qc, roomId, updated.id, documentId)) invalidateDd(qc, roomId);
      else patchDdRequest(qc, roomId, updated);
    },
  });
}

/** Unlink a document from a checklist item (DELETE
 * /:roomId/dd-requests/:requestId/documents/:documentId, idempotent) — also how
 * an AI match is rejected ("Not this one"): the API records the rejection and
 * drops the pair's suggestion. The document stays in the room; the item is
 * missing again once its last link is gone (unless marked provided). Patches the
 * item in place; refetches the summary and certifications. */
export function useUnlinkDdDocument(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, documentId }: { requestId: string; documentId: string }) =>
      api.delete<DdRequest>(`/rooms/${roomId}/dd-requests/${requestId}/documents/${documentId}`),
    onSuccess: (updated) => patchDdRequest(qc, roomId, updated),
  });
}

/** Section certifications (migration 0038): the room's seller attestations (🟢)
 * per DD section (GET /:roomId/certifications). Admin-or-seller read; the admin
 * view is READ-ONLY (only the bound seller can certify). Keyed on the opaque
 * `sectionKey` the UI forms (lib/section.ts). */
export function useRoomCertifications(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'certifications'],
    queryFn: () =>
      api
        .get<ListCertificationsResponse>(`/rooms/${roomId}/certifications`)
        // One list keyed by section: current attestations plus the ones a later
        // change invalidated (flagged, so the UI shows "Needs re-certification").
        .then((r) => [
          ...r.certifications,
          ...(r.needsRecertification ?? []).map((c) => ({ ...c, needsRecertification: true })),
        ]),
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// PHASE QUESTIONS (migration 0024) — the "Phase Questions" form. The intermediary
// defines free-text questions under a phase (admin-only CRUD) and views the
// seller's answers. All four mutations invalidate the room's `dd-questions`
// queries (prefix-matched, so every loaded phase's list refetches).
// ---------------------------------------------------------------------------

/** Invalidate every loaded phase-questions list for the room. */
function invalidateDdQuestions(qc: ReturnType<typeof useQueryClient>, roomId: string): void {
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-questions'] });
}

/** The questions defined under one phase (GET /:roomId/dd-questions?phaseId=),
 * ordered by question position, each with the seller's answer. Admin-or-seller
 * server-side; the admin sees every phase (no release-gating). */
/** Every phase's questions in one read (GET /:roomId/dd-questions, no phaseId) —
 * the checklist's "Questions for the seller" panel. Shares the `dd-questions`
 * key prefix, so every question mutation refetches it. */
export function useAllDdQuestions(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'dd-questions', 'all'],
    queryFn: () =>
      api
        .get<ListDdPhaseQuestionsResponse>(`/rooms/${roomId}/dd-questions`)
        .then((r) => r.questions),
    retry: false,
  });
}

export function useDdQuestions(roomId: string, phaseId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'dd-questions', phaseId],
    queryFn: () =>
      api
        .get<ListDdPhaseQuestionsResponse>(`/rooms/${roomId}/dd-questions?phaseId=${phaseId}`)
        .then((r) => r.questions),
    retry: false,
  });
}

/** Create a question under a phase (POST /:roomId/dd-phases/:phaseId/questions,
 * admin-only). `position` is auto-assigned server-side. */
export function useCreatePhaseQuestion(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ phaseId, body }: { phaseId: string; body: CreateDdPhaseQuestionRequest }) =>
      api.post<DdPhaseQuestion>(`/rooms/${roomId}/dd-phases/${phaseId}/questions`, body),
    onSuccess: () => invalidateDdQuestions(qc, roomId),
  });
}

/** Edit a question's prompt/position (PATCH /:roomId/dd-questions/:questionId,
 * admin-only). */
export function useUpdatePhaseQuestion(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      questionId,
      body,
    }: {
      questionId: string;
      body: UpdateDdPhaseQuestionRequest;
    }) => api.patch<DdPhaseQuestion>(`/rooms/${roomId}/dd-questions/${questionId}`, body),
    onSuccess: () => invalidateDdQuestions(qc, roomId),
  });
}

/** Delete a question (DELETE /:roomId/dd-questions/:questionId, admin-only). */
export function useDeletePhaseQuestion(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (questionId: string) =>
      api.delete<{ ok: true }>(`/rooms/${roomId}/dd-questions/${questionId}`),
    onSuccess: () => invalidateDdQuestions(qc, roomId),
  });
}

/** Set/clear a question's answer (PATCH /:roomId/dd-questions/:questionId/answer,
 * admin OR bound seller). The intermediary can override or clear a seller's
 * answer. An empty `answer` clears it back to unanswered. */
export function useAnswerPhaseQuestion(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      questionId,
      body,
    }: {
      questionId: string;
      body: AnswerDdPhaseQuestionRequest;
    }) => api.patch<DdPhaseQuestion>(`/rooms/${roomId}/dd-questions/${questionId}/answer`, body),
    onSuccess: () => invalidateDdQuestions(qc, roomId),
  });
}

// ---------------------------------------------------------------------------
// DD ACCOUNT ROSTER (ADR-0037) — the answers to the Financial roster questions.
// Adding, renaming or merging an account creates/relabels/retires checklist
// categories and items, so every write refreshes the roster AND the checklist
// reads (+ the category tree and certifications). The full number is fetched by
// `revealDdAccountNumber` (@dataroom/shared) on an explicit click only — never a
// cached query.
// ---------------------------------------------------------------------------

function invalidateDdAccounts(qc: ReturnType<typeof useQueryClient>, roomId: string): void {
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-accounts'] });
  invalidateDd(qc, roomId);
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'categories'] });
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'certifications'] });
}

/** The room's account kinds (with their placeholders) and roster
 * (GET /:roomId/dd-accounts). */
export function useDdAccounts(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'dd-accounts'],
    queryFn: () => api.get<ListDdAccountsResponse>(`/rooms/${roomId}/dd-accounts`),
    retry: false,
  });
}

/** Add an account (POST /:roomId/dd-accounts): 201 created, or 200 when it was
 * already on the roster (`created: false`). */
export function useCreateDdAccount(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDdAccountRequest) =>
      api.post<CreateDdAccountResponse>(`/rooms/${roomId}/dd-accounts`, body),
    onSuccess: () => invalidateDdAccounts(qc, roomId),
  });
}

/** Rename / correct an account (PATCH /:roomId/dd-accounts/:accountId). */
export function useUpdateDdAccount(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, body }: { accountId: string; body: UpdateDdAccountRequest }) =>
      api.patch<DdAccount>(`/rooms/${roomId}/dd-accounts/${accountId}`, body),
    onSuccess: () => invalidateDdAccounts(qc, roomId),
  });
}

/** Merge an account into another of the same kind (admin only,
 * POST /:roomId/dd-accounts/:accountId/merge) → the surviving account. */
export function useMergeDdAccount(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, intoAccountId }: { accountId: string; intoAccountId: string }) =>
      api.post<DdAccount>(`/rooms/${roomId}/dd-accounts/${accountId}/merge`, { intoAccountId }),
    onSuccess: () => {
      invalidateDdAccounts(qc, roomId);
      // The merged account's documents move to the survivor's categories.
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'documents'] });
    },
  });
}

// ---------------------------------------------------------------------------
// DD STATE ROSTER (issue #157) — the answer to the Financial roster question
// naming "[State]". Choosing or removing a state creates/removes checklist
// categories and items, so a write refreshes the checklist reads too.
// ---------------------------------------------------------------------------

/** The jurisdictions, the room's "[State]" placeholders and its chosen states. */
export function useDdStates(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'dd-states'],
    queryFn: () => api.get<ListDdStatesResponse>(`/rooms/${roomId}/dd-states`),
    retry: false,
  });
}

/** Set the chosen states (PUT /:roomId/dd-states) — the full set of codes. */
export function useSetDdStates(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (states: string[]) =>
      api.put<SetDdStatesResponse>(`/rooms/${roomId}/dd-states`, { states }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-states'] });
      invalidateDd(qc, roomId);
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'categories'] });
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'certifications'] });
    },
  });
}

/** PRD-0004 §3c: admin issues an NC/NDA envelope to a buyer against a room
 * document. On success the room's agreement listing refetches. */
export function useIssueEnvelope(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: IssueEnvelopeRequest) =>
      api.post<AdminSigningEnvelope>(`/rooms/${roomId}/signing/envelopes`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms', roomId, 'envelopes'] }),
  });
}

/** PRD-0004 §3c: admin cancels (voids) an issued agreement that hasn't sealed
 * yet — a cancelled envelope can never seal or advance the buyer's funnel. On
 * success the room's agreement listing refetches. */
export function useCancelEnvelope(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (envelopeId: string) =>
      api.post<CancelEnvelopeResponse>(`/rooms/${roomId}/signing/envelopes/${envelopeId}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms', roomId, 'envelopes'] }),
  });
}

/** The query string of the activity feed: only what is set; `before` pages. */
export function auditLogUrl(
  roomId: string,
  filters: AuditLogFilters = {},
  before: string | null = null,
): string {
  const q = new URLSearchParams();
  if (before) q.set('before', before);
  if (filters.includeAll) q.set('includeAll', '1');
  for (const key of ['person', 'document', 'item', 'milestone', 'folder', 'q'] as const) {
    const v = filters[key]?.trim();
    if (v) q.set(key, v);
  }
  if (filters.actions) q.set('actions', filters.actions.join(','));
  if (filters.qActions && filters.qActions.length > 0)
    q.set('qActions', filters.qActions.join(','));
  if (filters.from) q.set('from', filters.from);
  if (filters.to) q.set('to', filters.to);
  const qs = q.toString();
  return `/rooms/${roomId}/audit-log${qs ? `?${qs}` : ''}`;
}

/** PRD-0004 §3d Logs: the room's append-only, hash-chained audit log, newest
 * first, a page (200) at a time — `fetchNextPage` loads the next OLDER page
 * (keyset on the entry id). Admin-only server-side; view-only — there is no
 * mutation. `entries` is every page loaded so far, in order. By default the
 * server returns only what people did; `filters` (includeAll, searches, dates)
 * are applied server-side, so every page is a full page of what they allow. */
export function useAuditLog(roomId: string, filters: AuditLogFilters = {}) {
  const query = useInfiniteQuery({
    queryKey: ['rooms', roomId, 'audit-log', filters],
    queryFn: ({ pageParam }) =>
      api.get<ListAuditLogResponse>(auditLogUrl(roomId, filters, pageParam)),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextBefore ?? null,
    retry: false,
  });
  return { ...query, entries: query.data?.pages.flatMap((p) => p.entries) ?? [] };
}

/** "Contact support" (migration 0029): file a support request as the signed-in
 * actor. No room scoping and nothing to invalidate — this is a one-shot intake
 * with no listing anywhere in the app yet. */
export function useCreateSupportRequest() {
  return useMutation({
    mutationFn: (body: CreateSupportRequestRequest) =>
      api.post<SupportRequest>('/support-requests', body),
  });
}

// ---------------------------------------------------------------------------
// Tenant admin roster (ADR-0013) — the "Team" page. All three OWNER-ONLY
// server-side; a non-owner caller gets 403, which the page renders as a plain
// access message rather than the roster.
// ---------------------------------------------------------------------------

/** GET /tenant/admins — the tenant's admin roster (every owner + every
 * delegated rooms_admin) and the caller's own id. `retry: false` so a 401/403
 * surfaces immediately instead of retrying. */
export function useTenantAdmins() {
  return useQuery({
    queryKey: ['tenant-admins'],
    queryFn: () => api.get<ListTenantAdminsResponse>('/tenant/admins'),
    retry: false,
  });
}

/** POST /tenant/admins/:actorId/owner — make a firm admin an owner (ADR-0055). */
export function useMakeOwner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (actorId: string) =>
      api.post<SetTenantOwnerResponse>(`/tenant/admins/${actorId}/owner`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-admins'] }),
  });
}

/** DELETE /tenant/admins/:actorId/owner — step yourself down (immediate), or
 * request another owner's removal (pending 72 hours). 409 `last_owner` for the
 * firm's only owner. */
export function useRemoveOwner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (actorId: string) =>
      api.delete<SetTenantOwnerResponse>(`/tenant/admins/${actorId}/owner`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-admins'] }),
  });
}

/** POST /tenant/owner-removals/:id/cancel — cancel a pending owner removal
 * (the person being removed or any other owner). */
export function useCancelOwnerRemoval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (removalId: string) =>
      api.post<{ id: string; status: string }>(`/tenant/owner-removals/${removalId}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-admins'] }),
  });
}

/** GET /tenant/recovery-contact — the firm's recovery contact (owner-only; 403 otherwise). */
export function useRecoveryContact() {
  return useQuery({
    queryKey: ['recovery-contact'],
    queryFn: () => api.get<RecoveryContact>('/tenant/recovery-contact'),
    retry: false,
  });
}

/** PUT /tenant/recovery-contact — store an address as pending and email it a code. */
export function useSetRecoveryContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => api.put<RecoveryContact>('/tenant/recovery-contact', { email }),
    onSuccess: (data) => qc.setQueryData(['recovery-contact'], data),
  });
}

/** POST /tenant/recovery-contact/confirm — confirm the pending address with its code. */
export function useConfirmRecoveryContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      api.post<RecoveryContact>('/tenant/recovery-contact/confirm', { code }),
    onSuccess: (data) => qc.setQueryData(['recovery-contact'], data),
  });
}

/** DELETE /tenant/recovery-contact — remove the contact (and any pending change). */
export function useRemoveRecoveryContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<RecoveryContact>('/tenant/recovery-contact'),
    onSuccess: (data) => qc.setQueryData(['recovery-contact'], data),
  });
}

/** GET /tenant/classification-learning — the firm's cross-customer learning setting (owner-only). */
export function useClassificationLearning() {
  return useQuery({
    queryKey: ['classification-learning'],
    queryFn: () => api.get<ClassificationLearningSetting>('/tenant/classification-learning'),
    retry: false,
  });
}

/** PUT /tenant/classification-learning — opt the firm in or out. */
export function useSetClassificationLearning() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shareWithAllCustomers: boolean) =>
      api.put<ClassificationLearningSetting>('/tenant/classification-learning', {
        shareWithAllCustomers,
      }),
    onSuccess: (data) => qc.setQueryData(['classification-learning'], data),
  });
}

/** GET /rooms/:roomId/admins — the room's main admin and co-admins (migration
 * 0069), and whether the caller may add/remove co-admins. */
export function useRoomAdmins(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'admins'],
    queryFn: () => api.get<RoomAdminsResponse>(`/rooms/${roomId}/admins`),
    retry: false,
  });
}

/** POST /rooms/:roomId/admins — add a colleague as a co-admin by email (they are
 * emailed an invitation if they have no account yet). */
export function useAddRoomAdmin(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string }) =>
      api.post<RoomAdminsResponse>(`/rooms/${roomId}/admins`, body),
    onSuccess: (data) => qc.setQueryData(['rooms', roomId, 'admins'], data),
  });
}

/** DELETE /rooms/:roomId/admins/:actorId — remove a co-admin. */
export function useRemoveRoomAdmin(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (actorId: string) =>
      api.delete<RoomAdminsResponse>(`/rooms/${roomId}/admins/${actorId}`),
    onSuccess: (data) => qc.setQueryData(['rooms', roomId, 'admins'], data),
  });
}

/** POST /tenant/admins — grant the rooms_admin role to an actor by email.
 * Idempotent for an existing admin (200); 409 if the email is the tenant
 * owner. Invalidates the roster on success. */
export function useGrantAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GrantTenantAdminRequest) => api.post<TenantAdmin>('/tenant/admins', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant-plan'] });
      return qc.invalidateQueries({ queryKey: ['tenant-admins'] });
    },
  });
}

/** DELETE /tenant/admins/:actorId — revoke a delegated admin back to a
 * non-admin actor. 409 for the owner; 404 if not currently an admin.
 * Invalidates the roster on success. */
export function useRevokeAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (actorId: string) =>
      api.delete<RevokeTenantAdminResponse>(`/tenant/admins/${actorId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant-plan'] });
      return qc.invalidateQueries({ queryKey: ['tenant-admins'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Plan & billing (spec #140)
// ---------------------------------------------------------------------------

/** GET /tenant/plan — tier, limits and live usage. Tenant-admin-gated, so a
 * non-admin gets 403; `retry: false` so that (and a 401) surfaces immediately and
 * the trial banner / Billing page can simply stay quiet or show a plain message. */
export function useTenantPlan() {
  return useQuery({
    queryKey: ['tenant-plan'],
    queryFn: () => api.get<TenantPlan>('/tenant/plan'),
    retry: false,
  });
}

/** Send the browser to the Stripe-hosted page a billing endpoint returned. */
function redirectTo(res: BillingUrlResponse) {
  window.location.href = res.url;
}

// The four billing endpoints are OWNER-ONLY and answer 503 `billing_not_configured`
// while Stripe is dormant (surface via lib/plan `billingErrorMessage`). On success
// each returns `{ url }` and the browser redirects to it.

/** GET /tenant/billing/pricing — each paid plan's monthly and 12-month price for this firm's cohort, and the founding spots left. */
export function useBillingPricing() {
  return useQuery({
    queryKey: ['billing-pricing'],
    queryFn: () => api.get<BillingPricing>('/tenant/billing/pricing'),
    retry: false,
  });
}

/** POST /tenant/billing/checkout — start a subscription checkout for a paid tier (or move a live subscription to it). */
export function useBillingCheckout() {
  return useMutation({
    mutationFn: (body: BillingCheckoutRequest) =>
      api.post<BillingUrlResponse>('/tenant/billing/checkout', body),
    onSuccess: redirectTo,
  });
}

/** POST /tenant/billing/archive-pack — add archive packs (10 more archived rooms each). */
export function useBillingArchivePack() {
  return useMutation({
    mutationFn: (body: BillingArchivePackRequest) =>
      api.post<BillingUrlResponse>('/tenant/billing/archive-pack', body),
    onSuccess: redirectTo,
  });
}

/** POST /tenant/billing/recovery — pay the one-time recovery fee to restore a
 * trashed room. */
export function useBillingRecovery(roomId: string) {
  return useMutation({
    mutationFn: () =>
      api.post<BillingUrlResponse>('/tenant/billing/recovery', {
        roomId,
      } satisfies BillingRecoveryRequest),
    onSuccess: redirectTo,
  });
}

/** POST /tenant/billing/portal — open the Stripe billing portal (409 until the
 * tenant has subscribed once). */
export function useBillingPortal() {
  return useMutation({
    mutationFn: () => api.post<BillingUrlResponse>('/tenant/billing/portal'),
    onSuccess: redirectTo,
  });
}

// ---------------------------------------------------------------------------
// BUYER Q&A (ADR-0039) — the room's question queue. The admin sees every
// question, the asking buyer, the seller's drafts and the queue stage.
// ---------------------------------------------------------------------------

/** The room's buyer questions, newest first. `retry: false` so a 401 surfaces. */
export function useRoomQuestions(roomId: string) {
  return useQuery({
    queryKey: questionsQueryKey(roomId),
    queryFn: () => listQuestions(roomId).then((r) => r.questions),
    retry: false,
  });
}

/** One action on one question — answer (released at once), send to the seller,
 * release the seller's draft, close, reopen. */
export type QuestionAction =
  | { kind: 'answer'; questionId: string; answer: AnswerQuestionRequest }
  | { kind: 'release'; questionId: string; dropNotVisible?: boolean }
  | { kind: 'publish'; questionId: string; wording: PublishQuestionRequest }
  | { kind: 'route' | 'close' | 'reopen' | 'unpublish'; questionId: string };

/** Run a {@link QuestionAction}; the queue refetches after (RETURNED, so the
 * buttons stay disabled until the new state has landed). */
export function useQuestionAction(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: QuestionAction): Promise<QuestionView> => {
      switch (action.kind) {
        case 'answer':
          return answerQuestion(roomId, action.questionId, action.answer);
        case 'route':
          return routeQuestion(roomId, action.questionId);
        case 'release':
          return releaseAnswer(roomId, action.questionId, {
            dropNotVisible: action.dropNotVisible,
          });
        case 'close':
          return closeQuestion(roomId, action.questionId);
        case 'reopen':
          return reopenQuestion(roomId, action.questionId);
        case 'publish':
          return publishQuestion(roomId, action.questionId, action.wording);
        case 'unpublish':
          return unpublishQuestion(roomId, action.questionId);
      }
    },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: questionsQueryKey(roomId) }),
        qc.invalidateQueries({ queryKey: buyerQuestionsQueryKey(roomId) }),
      ]),
  });
}

/** Buyer-run rooms (ADR-0039): the admin asks the seller. The list refetches after (RETURNED). */
export function useAskSeller(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AskQuestionRequest) => askQuestion(roomId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: questionsQueryKey(roomId) }),
  });
}

// ---------------------------------------------------------------------------
// Weekly due-diligence digest (ADR-0047): the risks & issues log, timeline
// milestones, and the digest's settings + preview. All admin-only writes; the
// risks/milestones the bound seller may read are served by the same GETs.
// ---------------------------------------------------------------------------

/** GET /:roomId/risks — every entry (resolved too) plus, for the admin, the
 * people an entry may be assigned to (`ownerOptions`). */
export function useRisks(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'risks'],
    queryFn: () => api.get<ListRisksResponse>(`/rooms/${roomId}/risks`),
    retry: false,
  });
}

/** POST /:roomId/risks → 201 the new entry. */
export function useCreateRisk(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRiskRequest) => api.post<RoomRisk>(`/rooms/${roomId}/risks`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms', roomId, 'risks'] }),
  });
}

/** PATCH /:roomId/risks/:riskId — edit, or close (`status: 'resolved'`). There
 * is no delete: a closed entry stays on the record. */
export function useUpdateRisk(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ riskId, body }: { riskId: string; body: UpdateRiskRequest }) =>
      api.patch<RoomRisk>(`/rooms/${roomId}/risks/${riskId}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms', roomId, 'risks'] }),
  });
}

/** GET /:roomId/milestones — the admin-entered timeline dates, by date. */
export function useMilestones(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'milestones'],
    queryFn: () =>
      api.get<ListMilestonesResponse>(`/rooms/${roomId}/milestones`).then((r) => r.milestones),
    retry: false,
  });
}

/** POST /:roomId/milestones → 201 the new milestone. */
export function useCreateMilestone(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMilestoneRequest) =>
      api.post<RoomMilestone>(`/rooms/${roomId}/milestones`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms', roomId, 'milestones'] }),
  });
}

/** PATCH /:roomId/milestones/:milestoneId. */
export function useUpdateMilestone(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ milestoneId, body }: { milestoneId: string; body: UpdateMilestoneRequest }) =>
      api.patch<RoomMilestone>(`/rooms/${roomId}/milestones/${milestoneId}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms', roomId, 'milestones'] }),
  });
}

/** DELETE /:roomId/milestones/:milestoneId → 204. */
export function useDeleteMilestone(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (milestoneId: string) =>
      api.delete<void>(`/rooms/${roomId}/milestones/${milestoneId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms', roomId, 'milestones'] }),
  });
}

/** GET /:roomId/digest-settings — settings, the room timezone, the next send
 * and who will receive it. */
export function useDigestSettings(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'digest-settings'],
    queryFn: () => api.get<DigestSettingsView>(`/rooms/${roomId}/digest-settings`),
    retry: false,
  });
}

/** PUT /:roomId/digest-settings (all seven fields) → the refreshed view, written
 * straight into the cache so the recipients list updates with no refetch. */
export function useSaveDigestSettings(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DigestSettings) =>
      api.put<DigestSettingsView>(`/rooms/${roomId}/digest-settings`, body),
    onSuccess: (view) => qc.setQueryData(['rooms', roomId, 'digest-settings'], view),
  });
}

/** GET /:roomId/digest/preview?as= — the digest as it would go out now, to the
 * admin or to the bound seller. Fetched only while the preview is open, and
 * always fresh (it reflects the room as of now). */
export function useDigestPreview(roomId: string, as: 'admin' | 'seller', enabled: boolean) {
  return useQuery({
    queryKey: ['rooms', roomId, 'digest-preview', as],
    queryFn: () => api.get<DigestPreview>(`/rooms/${roomId}/digest/preview?as=${as}`),
    enabled,
    retry: false,
    staleTime: 0,
  });
}

/** POST /:roomId/digest/preview → 202: email the admin their own copy now. */
export function useSendDigestPreview(roomId: string) {
  return useMutation({
    mutationFn: () => api.post<SendDigestPreviewResponse>(`/rooms/${roomId}/digest/preview`),
  });
}

// ---------------------------------------------------------------------------
// Contract expiration register (ADR-0054): the room's contracts with the dates
// each document states, corrections (audited) and the admin's "Re-read".
// ---------------------------------------------------------------------------

/** GET /:roomId/contracts — the register (the admin sees every contract). */
export function useContractRegister(roomId: string) {
  return useQuery({
    queryKey: contractsQueryKey(roomId),
    queryFn: () => fetchContractRegister(roomId),
    retry: false,
  });
}

/** PATCH /:roomId/contracts/:documentId — a correction; the register refreshes. */
export function useCorrectContract(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, body }: { documentId: string; body: CorrectContractRequest }) =>
      correctContract(roomId, documentId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: contractsQueryKey(roomId) }),
  });
}

/** POST /:roomId/contracts/rescan — re-read every contract (one background job each). */
export function useRescanContracts(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => rescanContracts(roomId),
    onSuccess: () => qc.invalidateQueries({ queryKey: contractsQueryKey(roomId) }),
  });
}

/** ADR-0056: GET /:roomId/activity-alerts — the calling admin's own "buyer
 * opened X" email for this room (off until they turn it on). */
export function useActivityAlerts(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'activity-alerts'],
    queryFn: () => api.get<ActivityAlertSettingsView>(`/rooms/${roomId}/activity-alerts`),
    retry: false,
  });
}

/** ADR-0056: PUT /:roomId/activity-alerts → the saved view, written into the cache. */
export function useSaveActivityAlerts(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ActivityAlertSettings) =>
      api.put<ActivityAlertSettingsView>(`/rooms/${roomId}/activity-alerts`, body),
    onSuccess: (view) => qc.setQueryData(['rooms', roomId, 'activity-alerts'], view),
  });
}
